import { useState, useEffect } from 'react';
import { Table, DatePicker, Select, Button, Modal, Form, Input, Tag, message } from 'antd';
import { getData, createData, updateData, deleteData } from '../api/api';
import {SearchOutlined} from "@ant-design/icons";
import { useTranslation } from 'react-i18next';

const { Search } = Input;

const BankAccounts = ({isAdmin}) => {
    const { t } = useTranslation();

    const [data, setData] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [filters, setFilters] = useState({});
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
    const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [statusChangeRecord, setStatusChangeRecord] = useState(null);
    const [form] = Form.useForm();
    const [updateForm] = Form.useForm();

    const columns = [
        {
            title: '№',
            key: 'index',
            width: 60, // Aniqlik uchun kenglik
            render: (_, __, index) => index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        { title: t('bankAccounts.name'), dataIndex: 'name', key: 'name', width: 150 },
        { title: t('bankAccounts.bankName'), dataIndex: 'bankName', key: 'bankName', width: 150 },
        { title: t('bankAccounts.accountNumber'), dataIndex: 'bankAccount', key: 'bankAccount', width: 200 }, // Uzunroq bo'lishi mumkin
        { title: t('bankAccounts.mfo'), dataIndex: 'bankMfo', key: 'bankMfo', width: 120 },
        { title: t('bankAccounts.tin'), dataIndex: 'tin', key: 'tin', width: 100 },
        { title: t('bankAccounts.merchantName'), dataIndex: 'merchantName', key: 'merchantName', width: 200 },
        { title: t('bankAccounts.createdAt'), dataIndex: 'createdAt', key: 'createdAt', width: 200 },
        { title: t('bankAccounts.updatedAt'), dataIndex: 'updatedAt', key: 'updatedAt', width: 200 },
        {
            title: t('bankAccounts.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) =>
                status === 0 ? (
                    <Tag color="green">{t('bankAccounts.active')}</Tag>
                ) : (
                    <Tag color="red">{t('bankAccounts.inactive')}</Tag>
                ),
        }
    ];

    if (isAdmin) {
        columns.push({
            title: t('bankAccounts.actions'),
            key: 'actions',
            width: 350,
            render: (_, record) => (
                <>
                    <Button size="small" onClick={() => handleUpdate(record)}>
                        {t('bankAccounts.edit')}
                    </Button>
                    <Button
                        size="small"
                        danger={record.status === 0}
                        type={record.status === 0 ? "primary" : "default"}
                        onClick={() => showStatusChangeModal(record)}
                        style={{ marginLeft: 8 }}
                    >
                        {record.status === 0 ? t('bankAccounts.deactivate') : t('bankAccounts.activate')}
                    </Button>
                </>
            ),
        });
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(isAdmin ? '/admin/dashboard/bank-account-list-by-filter' :
                '/merchant/dashboard/bank-account-list-by-filter', {
                page: pagination.current - 1, // Backend uchun 0 dan boshlanadi
                size: pagination.pageSize,
                status: -1, // Default status
                ...filters,
            });
            setData(response.data?.data?.content || []);
            setPagination({
                ...pagination,
                total: response.data?.data?.totalCount,
            });
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    const getMerchants = async () => {
        try {
            const response = await getData('/admin/dashboard/merchants-list-by-filter', {
                page: 0,
                size: 1000000, // Keyinchalik yangi API chiqadi
                status: -1, // Default status
            });
            setMerchants(response.data?.data?.content || []);
        } catch (error) {
            console.error(error);
        }
    };

    const getBanks = async () => {
        if (banks.length===0){
            try {
                const response = await getData('/admin/bank/list', {});
                setBanks(response.data?.data || []);
            } catch (error) {
                console.error(error);
            }
        }
    };

    useEffect(() => {
        fetchData();
        if (isAdmin && (!merchants || merchants.length === 0)) {
            getMerchants();
        }
    }, [pagination.current, pagination.pageSize, filters]);

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const handleFilter = (values) => {
        setFilters(values);
        setPagination({ ...pagination, current: 1 }); // Filtr o'zgarganda 1-sahifaga qaytish
    };

    const handleCreate = async (values) => {
        try {
            const response = await createData("/admin/bank-account/create", values);
            if (response?.data?.success && response?.data?.data) {
                fetchData();
                message.success(t('bankAccounts.successAdd'));
                handleCancel();
            } else {
                alert(response?.data?.error?.text || t('bankAccounts.errorAdd'))
                message.error(response?.data?.error?.text || t('bankAccounts.errorAdd'));
            }
        } catch (error) {
            console.error(error);
            alert(t('bankAccounts.errorOccurred'));
            message.error(t('bankAccounts.errorOccurred'));
        }
    };

    const handleUpdate = (record) => {
        setCurrentRecord(record);
        updateForm.setFieldsValue({
            name: record.name,
            bankName: record.bankName,
            bankAccount: record.bankAccount,
            bankMfo: record.bankMfo,
            tin: record.tin,
            merchantId: record.merchantId
        });
        setIsUpdateModalVisible(true);
    };

    const handleUpdateSubmit = async (values) => {
        try {
            const response = await createData('/admin/bank-account/update', {
                ...values,
                id:currentRecord.id
            });
            if (response?.data?.success && response?.data?.data) {
                fetchData();
                message.success(t('bankAccounts.successUpdate'));
                handleUpdateCancel();
            } else {
                alert(response?.data?.error?.text || t('bankAccounts.updateError'))
                message.error(response?.data?.error?.text || t('bankAccounts.updateError'));
            }
        } catch (error) {
            console.error(error);
            alert(t('bankAccounts.updateError'));
            message.error(t('bankAccounts.updateError'));
        }
    };

    const showStatusChangeModal = (record) => {
        setStatusChangeRecord(record);
        setIsStatusModalVisible(true);
    };

    const handleStatusChangeCancel = () => {
        setIsStatusModalVisible(false);
        setStatusChangeRecord(null);
    };

    const handleStatusChange = async () => {
        try {
            // Status ni teskarisiga o'zgartirish (0 => 1, 1 => 0)
            const newStatus = statusChangeRecord.status === 0 ? 1 : 0;

            const response = await createData('/admin/bank-account/update', {
                ...statusChangeRecord,
                status: newStatus
            });

            if (response?.data?.success) {
                fetchData();
                message.success(t('bankAccounts.statusChanged'));
                handleStatusChangeCancel();
            } else {
                alert(response?.data?.error?.text || t('bankAccounts.statusChangeError'))
                message.error(response?.data?.error?.text || t('bankAccounts.statusChangeError'));
            }
        } catch (error) {
            alert(t('bankAccounts.statusChangeError'))
            console.error(error);
            message.error(t('bankAccounts.statusChangeError'));
        }
    };

    const showModal = () => {
        setIsModalVisible(true);
        getBanks();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleUpdateCancel = () => {
        setIsUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentRecord(null);
    };

    // Merchant tinni ID orqali topish uchun funksiya
    const getMerchantTinById = (id) => {
        const merchant = merchants.find((m) => m.id === id);
        return merchant ? merchant.tin : '';
    };

    const getBankNameByMfo = (mfo) => {
        const bank = banks.find((b) => b.mfo === mfo);
        return bank ? bank.name : '';
    };

    const handleMerchantChange = (value) => {
        const tin = getMerchantTinById(value);
        form.setFieldsValue({ merchantId: value, tin }); // TIN ni avtomatik o'rnatish
    };

    const handleMfoChange = (e) => {
        const mfo = e.target.value;
        if (mfo.length===5){
            const bankName = getBankNameByMfo(mfo);
            form.setFieldsValue({ bankMfo: mfo, bankName });
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>{t('bankAccounts.title')}</h2>
                {
                    isAdmin ?
                        <Button type="primary" onClick={showModal}>
                            {t('bankAccounts.add')}
                        </Button> : ''
                }
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {isAdmin ?
                    <Select
                        style={{ width: 120 }}
                        defaultValue={null}
                        onChange={(value) => handleFilter({ ...filters, merchantId: value })}
                        size="middle"
                        showSearch
                        placeholder={t('bankAccounts.merchantsPlaceholder')}
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        notFoundContent={t('bankAccounts.notFound')}
                        dropdownStyle={{ zIndex: 9999 }}
                        getPopupContainer={(trigger) => trigger.parentNode}

                    >
                        {/*<Select.Option value={null}>Merchantlar</Select.Option>*/}
                        {merchants?.map((value) => (
                            <Select.Option key={value.id} value={value.id}>
                                {value.name}
                            </Select.Option>
                        ))}
                    </Select> : ''}

                <Search
                    placeholder={t('bankAccounts.searchPlaceholder')}
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="middle"
                    style={{ width: 300 }}
                    onSearch={(value) => handleFilter({ ...filters, search: value })}
                />
            </div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={{
                    ...pagination,
                    showTotal: (total) => t('bankAccounts.total', { total })
                }}
                loading={loading}
                onChange={handleTableChange}
                rowKey="id"
                scroll={{ x: 'max-content' }} // Gorizontal scroll uchun
            />

            {/* Yangi hisob qo'shish modali */}
            <Modal
                title={t('bankAccounts.addTitle')}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t('bankAccounts.name')}
                        rules={[{ required: true, message: t('bankAccounts.nameRequired') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="bankMfo"
                        label={t('bankAccounts.mfo')}
                        rules={[{ required: true, message: t('bankAccounts.mfoRequired') }]}
                    >
                        <Input onChange={handleMfoChange}/>
                    </Form.Item>

                    <Form.Item
                        name="bankName"
                        label={t('bankAccounts.bankName')}
                        rules={[{ required: true, message: t('bankAccounts.bankNameRequired') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="bankAccount"
                        label={t('bankAccounts.accountNumber')}
                        rules={[{ required: true, message: t('bankAccounts.accountNumberRequired') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="merchantId"
                        label={t('bankAccounts.merchant')}
                        rules={[{ required: true, message: t('bankAccounts.merchantRequired') }]}
                    >
                        <Select
                            placeholder={t('bankAccounts.selectMerchant')}
                            onChange={handleMerchantChange}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                            notFoundContent={t('bankAccounts.notFound')}
                            dropdownStyle={{ zIndex: 9999 }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                        >
                            {merchants?.map((merchant) => (
                                <Select.Option key={merchant.id} value={merchant.id}>
                                    {merchant.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="tin"
                        label={t('bankAccounts.tin')}
                        rules={[{ required: true, message: t('bankAccounts.tinRequired') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('bankAccounts.save')}
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                            {t('bankAccounts.cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* O'zgartirish modali */}
            <Modal
                title={t('bankAccounts.updateTitle')}
                visible={isUpdateModalVisible}
                onCancel={handleUpdateCancel}
                footer={null}
            >
                <Form form={updateForm} onFinish={handleUpdateSubmit} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t('bankAccounts.name')}
                        rules={[{ required: true, message: t('bankAccounts.nameRequired') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="bankName"
                        label={t('bankAccounts.bankName')}
                        rules={[{ required: true, message: t('bankAccounts.bankNameRequired') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('bankAccounts.save')}
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleUpdateCancel}>
                            {t('bankAccounts.cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Holat o'zgartirish tasdiq modali */}
            <Modal
                title={t('bankAccounts.statusTitle')}
                visible={isStatusModalVisible}
                onCancel={handleStatusChangeCancel}
                footer={[
                    <Button key="cancel" onClick={handleStatusChangeCancel}>
                        {t('bankAccounts.cancel')}
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleStatusChange}>
                        {t('bankAccounts.confirm')}
                    </Button>
                ]}
            >
                <p>
                    {t('bankAccounts.statusQuestion', {
                        name: statusChangeRecord?.name,
                        status: statusChangeRecord?.status === 0 ? t('bankAccounts.inactive') : t('bankAccounts.active')
                    })}
                </p>
            </Modal>
        </div>
    );
};
export default BankAccounts;